<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template>
	<k-dialog v-model="dialog_open" max-width="1200" content-class="k-tall-dialog" persistent scrollable>
		<k-card class="blue lighten-5">
			<k-card-title class="py-2" xstyle="border-bottom:1px solid #ccc">
				<b style="font-size:20px"><k-icon color="blue darken-3" class="mr-2">fas fa-flag-checkered</k-icon><span v-html="title_display"></span></b>
				<!-- <k-spacer/> -->

				<div v-if="initialized" class="d-flex align-center ml-12">
					<k-btn class="mr-1" x-small outlined fab color="blue darken-3" style="background-color:#fff" dark @click="increment_date(-1)"><k-icon small>fas fa-arrow-left</k-icon></k-btn>

					<k-menu ref="date_menu" v-model="date_menu" transition="scale-transition" offset-y min-width="290px">
						<template v-slot:activator="{on}">
							<k-btn v-on="on" color="blue darken-3" class="k-tight-btn k-nocaps-btn elevation-0" outlined style="background-color:#fff">
								<span style="font-weight:900; font-size:20px" class="blue--text text--darken-3 mr-3">{{daily_lesson_date_display}}</span>
								<k-icon color="blue darken-3" style="font-size:20px; margin-top:-2px;">fas fa-calendar-day</k-icon>
							</k-btn>
						</template>
						<k-date-picker v-model="daily_lesson_date" no-title scrollable>
							<k-spacer></k-spacer>
							<k-btn text color="primary" @click="date_menu=false">Done</k-btn>
						</k-date-picker>
					</k-menu>

					<k-btn class="ml-1" x-small outlined style="background-color:#fff" fab color="blue darken-3" dark @click="increment_date(1)"><k-icon small>fas fa-arrow-right</k-icon></k-btn>
				</div>

				<k-spacer/>
				<!-- <v-btn v-if="initialized" color="secondary" dark class="k-tight-btn mr-2" @click="show_report_link"><k-icon small class="mr-2">fas fa-link</k-icon>Share Link</v-btn> -->
				<k-btn color="primary" @click="close_dialog"><k-icon small class="mr-2">fas fa-check</k-icon>Done</k-btn>
			</k-card-title>
			<k-card-text v-if="!initialized" class="mt-2 text-center" style="font-size:20px; min-height:200px; padding-top:32px;">Loading lesson data...</k-card-text>
			<!-- <k-card-text v-if="initialized&&lessons.length==0" class="mt-2 text-center" style="font-size:20px; min-height:200px; padding-top:32px;">No lessons for this date</k-card-text> -->
			<k-card-text v-if="initialized" class="ma-0 px-2 pt-0 pb-2" style="font-size:16px">
				<k-tabs v-model="tab" class="k-daily-lessons-tabs">
					<k-tab v-for="(tab_item, i) in tab_items" :key="tab_item.tab_key" active-class="k-daily-lessons-active-tab" class="k-tight-btn k-nocaps-btn" :style="tab_style(tab_item, i)">
						<div v-if="i==0" style="width:60px" class="text-center">
							<k-icon color="#fff" style="font-size:20px;">fas fa-home</k-icon>
						</div>
						<div v-if="i > 0 && i != tab" v-html="tab_item.tab_text"></div>
						<div v-if="i > 0 && i == tab">
							<k-btn xtyle="`color:${tab_item.contrast_color}; font-size:14px;`" color="#fff" small class="elevation-0 k-tight-btn k-nocaps-btn" @click="open_collection(tab_item.c)">
								<k-icon small class="mr-2">fas fas fa-chalkboard</k-icon>
								<span v-html="tab_item.c.title"></span>
							</k-btn>
						</div>
					</k-tab>
				</k-tabs>
				<k-tabs-items v-model="tab">
					<k-tab-item v-for="(tab_item, i) in tab_items" :key="tab_item.tab_key" transition="false"><div class="k-daily-lessons-body-outer">
						<!-- "Home" for DailyLessons -->
						<div v-if="i == 0" class="pa-3" style="font-size:18px;">
							<div v-if="lessons.length==0" class="text-center mt-4">
								<p>You have not created have any Lesson Plans tagged to this date.</p>
								<p class="mt-3">To create Lesson Plans... <i>INSTRUCTIONS TO COME...</i></p>
							</div>
							<div v-else class="text-center mt-4">
								<p>You have <b>{{lessons.length}} Lesson {{U.ps('Plan', lessons.length)}}</b> tagged to this date ({{daily_lesson_date_display}}).</p>
								<p class="mt-3">Click a tab above to view, edit, or print a Lesson Plan.</p>
							</div>
						</div>

						<!-- A lesson -->
						<div v-else>
							<div class="k-daily-lessons-lesson-header d-flex align-center" :style="`background-color:${tab_item.color}; color:${tab_item.contrast_color};`">
								<span class="mr-2" v-html="tab_item.l.date_header()"></span>
								<b v-html="tab_item.l.lesson_title"></b>
								<k-spacer/>
								<k-btn v-if="user_can_edit" fab x-small color="#000" style="background-color:#fff" outlined dark @click="edit_lesson"><k-icon color="blue darken-2" small>fas fa-edit</k-icon></k-btn>
								<k-btn class="ml-2" fab x-small color="#000" style="background-color:#fff" outlined dark @click="print_lesson"><k-icon small>fas fa-print</k-icon></k-btn>
							</div>
							<LessonView v-if="!lesson_being_edited" :lesson="tab_item.l" :print_lesson_dom_id="print_lesson_dom_id+tab_item.l.lesson_id" :show_print_dialog="show_print_dialog" />
							<LessonPrint v-if="show_print_dialog" :lesson="tab_item.l" :print_lesson_dom_id="print_lesson_dom_id+tab_item.l.lesson_id" @dialog_cancel="show_print_dialog=false" />
							<div v-if="lesson_being_edited" class="k-lesson-edit-outerx">
								<LessonEditorEnhanced ref="lesson_editor_component" :original_lesson="tab_item.l" :original_lp_variant="'B'" :lesson_class="'teacher'" :course_code="tab_item.c.course_code" :lp_unit_id="tab_item.cam.unit_id" :lesson_shifted="false" @shift_lesson="" @edit_lesson_cancel="edit_lesson_cancel" @edit_lesson_saved="edit_lesson_saved" />
							</div>
						</div>
					</div></k-tab-item>
				</k-tabs-items>
			</k-card-text>
		</k-card>
	</k-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CASEItemBtn from '../standards/CASEItemBtn'
import LessonView from '../lessons/LessonView'
import LessonPrint from '../lessons/LessonPrint'
import LessonEditorEnhanced from '../lessons/LessonEditorEnhanced'

export default {
	components: { CASEItemBtn, LessonView, LessonPrint, LessonEditorEnhanced },
	mixins: [],
	props: {
		// lesson_report_id: { type: String, required: true },
		// nreq: { type: String, required: false, default() { return ''} },
	},
	data() { return {
		dialog_open: true,
		initialized: false,
		tab: null,
		date_menu: false,
		show_print_dialog: false,
		print_lesson_dom_id: U.new_uuid(),
		lesson_being_edited: false,
	}},
	computed: {
		...mapState(['my_lessons', 'my_resources', 'my_ca_mappings', 'all_courses', 'user_records', 'user_info', 'daily_lesson_current_tab']),
		...mapGetters(['role', 'system_role', 'user_is_principal_or_ap']),
		daily_lesson_date: {
			get() { return this.$store.state.daily_lesson_date },
			set(val) { this.$store.commit('set', ['daily_lesson_date', val]) }
		},
		daily_lesson_date_o() {
			return date.parse(this.daily_lesson_date, 'YYYY-MM-DD')
		},
		daily_lesson_date_display() {
			return date.format(this.daily_lesson_date_o, 'dddd, MMMM D')
			// return date.format(this.daily_lesson_date_o, 'ddd MMM D')
		},
		title_display() {
			return `Daily Lesson Plans`
		},
		lessons() {
			// get user's lessons for the selected date
			return this.my_lessons.filter(lesson=> {
				// if no date it can't be for today
				if (empty(lesson.lesson_date)) return false

				// if start date === today, it *is* for today
				if (lesson.lesson_date == this.daily_lesson_date) return true

				// if start date is > today it can't be for today
				if (lesson.lesson_date > this.daily_lesson_date) return false

				// if end date is set...
				if (!empty(lesson.lesson_date_end)) {
					// then if end date is < today it's not for today
					if (lesson.lesson_date_end < this.daily_lesson_date) return false

					// otherwise it *is* for today
					return true
				}
				return false
			})
		},
		tab_items() {
			if (!this.initialized) return []

			let arr = []

			arr.push({
				tab_key: '0',
				tab_text: 'Home',
				color: '#666',
				contrast_color: '#fff',
			})

			for (let lesson of this.lessons) {
				// find the collection for this lesson
				let cam = this.my_ca_mappings.find(x=>(x.asset_type == 'lesson' && x.asset_id == lesson.lesson_id))
				let collection = this.all_courses.find(x=>x.lp_id == cam?.collection_id)
				let color = collection ? U.get_collection_color(collection) : '#000'
				let light_color = collection ? U.get_collection_color(collection, 'light') : '#eee'
				let contrast_color = U.get_contrast_color(color)
				let o = {
					tab_key:lesson.lesson_id,
					type: 'lesson',
					tab_text: `<div class="px-0">${lesson.lesson_title}</div>`,
					l: lesson,	// use `l` for the lesson reference to ease coding
					cam: cam ?? {},
					c: collection ?? {},	// use `c` for the collection reference to ease coding
					color: color,
					light_color: light_color,
					contrast_color: contrast_color,
				}
				arr.push(o)
			}
			return arr
		},
		all_standards() {
			let arr = []
			for (let tab_item of this.tab_items) {
				if (tab_item.type != 'lesson') continue
				for (let standard of tab_item.l.standards) {
					if (arr.find(x=>x.identifier == standard.identifier)) continue
					// console.log(standard)
					arr.push(standard)
				}
			}

			arr.sort((a,b)=>U.natural_sort(a.humanCodingScheme, b.humanCodingScheme))
			return arr
		},
		selected_tab_item() {
			return this.tab_items[this.tab]
		},
		user_can_edit() {
			return true
		},
	},
	watch: {
		tab() {
			// store daily_lesson_current_tab so we can return to this tab when we re-initialize
			this.$store.commit('set', ['daily_lesson_current_tab', this.selected_tab_item.tab_key])
		},
	},
	created() {
	},
	mounted() {
		vapp.daily_lessons_component = this
		this.initialize()
	},
	methods: {
		async initialize() {
			U.loading_start()
			// get_all_courses if we haven't already
			if (!this.$store.state.all_courses_loaded) {
				await this.$store.dispatch('get_all_courses')
			}

			// get lesson_masters if we don't already have them
			await this.$store.dispatch('get_lesson_masters')

			// if daily_lesson_date isn't set, set to today
			if (empty(this.daily_lesson_date)) {
				this.daily_lesson_date = date.format(new Date(), 'YYYY-MM-DD')

				// FOR TESTING
				this.daily_lesson_date = '2025-08-06'
			}

			this.initialized = true
			U.loading_stop()

			// we have to do this with a timeout
			let daily_lesson_current_tab = this.daily_lesson_current_tab
			if (!empty(daily_lesson_current_tab)) {
				setTimeout(x=>{
					let i = this.tab_items.findIndex(x=>x.tab_key == daily_lesson_current_tab)
					if (i > -1) {
						this.tab = i
					}
				})
			}
		},

		tab_style(tab_item, i) {
			let s = ''
			if (true || i == this.tab) {
				s += ` color:${tab_item.contrast_color}; background-color:${tab_item.color};`
			} else {
				s += ` color:${tab_item.color}; background-color:${tab_item.light_color};`
			}
			return s
		},	

		rendered_standard(standard) {
			let s = ''
			if (standard.humanCodingScheme) s += `<p><b class="mr-2">${standard.humanCodingScheme}</b></p>`
			s += U.marked_latex(standard.fullStatement)
			return s
		},

		standard_clicked(case_item) {
			// show the framework, highlight this item, and send in selected_items and limit_to_selected_items if there
			let data = { 
				framework_identifier: case_item.framework_identifier, 
				item_identifier: case_item.identifier, 
				no_framework_reset: true,
			}

			if (!data.framework_identifier) {
				// shouldn't happen I think...
				console.warn('standard_clicked; couldn’t get framework_identifier', case_item)
			}

			// add all standards as selected_items
			if (this.all_standards.length > 0) {
				data.selected_items = []
				for (let case_item of this.all_standards) data.selected_items.push(case_item.identifier)
				data.limit_to_selected_items = 'only'
			}

			vapp.$refs.satchel.execute('show').then(()=>{
				vapp.$refs.satchel.execute('load_framework', data).then(()=>{
				})
			})
		},

		editor_config(heightMin = 250) {
			let config = U.get_froala_config({
				placeholderText: '',
				// initOnClick: true,
				toolbarInline: false,
				paragraphFormat: {
					H3: 'Section Header',
					N: 'Normal',
					BLOCKQUOTE: 'Block Quote',
				    PRE: 'Code',
				},
				heightMin: heightMin,
			})

			// add the insert resource btn at the front of the moreRich buttons
			config.toolbarButtons.moreRich.buttons.unshift('insert_resource')
			config.toolbarButtons.moreRich.buttonsVisible += 1

			return config
		},

		show_report_link() {
			let msg = '<div class="mb-2">The following URL will take users directly to this Weekly Lesson Plan:</div>'
			msg += `<div class="text-center" style="font-size:16px"><b>${this.lesson_report_url}</b></div>`

			this.$confirm({
				title: 'Share Weekly Lesson Plan Link',
				text: msg,
				cancelText: 'Copy Link URL',
				cancelIcon: 'fas fa-copy',
				acceptIcon: 'fas fa-check',
				dialogMaxWidth: 640,
			}).catch(e=>{
				U.copy_to_clipboard(this.lesson_report_url)
				this.$inform('Link copied to clipboard')
			})
		},

		open_collection(collection) {
			let route = collection.vue_route()
			// add the unit to the route if we know it
			if (this.selected_tab_item?.cam?.unit_id*1 > 0) {
				route = route.replace(/0$/, this.selected_tab_item.cam.unit_id)
			}
			this.$router.push({ path: route })
			this.close_dialog()
		},

		increment_date(delta) {
			let updated_date = new Date(this.daily_lesson_date_o)
			updated_date.setDate(updated_date.getDate() + delta)
			this.daily_lesson_date = date.format(updated_date, 'YYYY-MM-DD')
		},

		edit_lesson() {
			this.lesson_being_edited = true
		},

		edit_lesson_cancel() {
			this.lesson_being_edited = false
		},

		edit_lesson_saved(args) {
			console.warn(args)
			// apply *_showing values from edited_lesson if we have it
			if (args.edited_lesson) {
				args.updated_lesson.resources_showing = args.edited_lesson.resources_showing
				args.updated_lesson.standards_showing = args.edited_lesson.standards_showing
				args.updated_lesson.student_description_showing = args.edited_lesson.student_description_showing
				for (let i = 0; i < args.edited_lesson.lesson_plan.length; ++i) {
					args.updated_lesson.lesson_plan[i].lc_showing = args.edited_lesson.lesson_plan[i].lc_showing
					args.updated_lesson.lesson_plan[i].lc_open_for_editing = args.edited_lesson.lesson_plan[i].lc_open_for_editing
				}
			}

			// call update_content_item mutation to make sure the item's changes are reflected everywhere
			let l = new Lesson(args.updated_lesson)
			this.$store.commit('update_content_item', l)

			// if the flag is 'and_close', close the editor
			if (args.flag == 'and_close') {
				this.edit_lesson_cancel()
			}
		},

		print_lesson() {
			this.show_print_dialog = true
		},

		close_dialog() {
			this.dialog_open = false
			this.$emit('dialog_cancel')
		},
	}
}
</script>

<style lang="scss">
.k-daily-lessons-tabs {
	border-radius:8px 8px 0 0;
	// border-top:1px solid $v-blue-darken-3;
	// border-left:1px solid $v-blue-darken-3;
	// border-right:1px solid $v-blue-darken-3;
	margin-bottom:-2px;

	.v-tabs-bar {
		height:42px;
		background-color:transparent!important;
	}

	.v-tab {
		border-radius:8px 8px 0 0;
		min-width:auto;
		font-weight:bold;
		font-size:14px;
		border-top:1px solid #999;
		border-left:1px solid #999;
		border-right:1px solid #999;
		border-bottom:1px solid #999;
		margin-bottom:2px;
		padding:0 12px;
	}
}

.k-daily-lessons-active-tab {
	// border-radius:7px 7px 0 0;
	// background-color:$v-blue-darken-3;
	// color:#fff!important;
	// color:transparent!important;
	font-weight:900;
	border-bottom: 0px solid transparent!important;
	// color:transparent!important;
}

.k-daily-lessons-lesson-header {
	padding:8px 8px;
	border-left:1px solid #999;
	border-right:1px solid #999;
	// border-top:1px solid #999;
}

.k-daily-lessons-body-outer {
	// border-left:1px solid $v-blue-darken-3;
	// border-right:1px solid $v-blue-darken-3;
	// border-bottom:1px solid $v-blue-darken-3;
	padding:0px;
	// border-radius:0 0 8px 8px;
	min-height:300px;
	background-color:#ccc;

	.k-lpe-alba-outer-outer {
		max-height:calc(100vh - 164px);
	}
	.k-lpe-lesson-edit {
		.k-lpe-alba-outer-outer {
			max-height:calc(100vh - 234px);
			margin:0 0 5px 0!important;
		}
		.k-lpe-lesson-editor-buttons {
			margin:0!important;
			border-radius:0;
			border:1px solid #999;
		}
	}
}

.k-daily-lessons-standard:hover {
	text-decoration:underline;
	cursor:pointer;
}

</style>
